import { useState } from "react";

import { makeUnique } from "@nens/shared-utils";

// Track which table rows are checked.

// Some business rules:
// - If all rows are checked, the "selectAll" variable should be true.
// - If some rows are checked, the "someChecked" variable should be true.
// - A row is identified by an id, which can be a property of the TableRowType or a function that returns the id.
// - The defaultSelected variable is an array of ids that should be checked by default.
// - The checkedRows variable is an array of the rows that are checked.
// - The deselectAll function unchecks all rows.
// - The setAllChecked function checks or unchecks all rows.
// - Tables can have multiple pages. We don't track checked items on other pages, because we don't know
//   if they are still in the list of items when filters change. We only track the checked items on the current page.

export function useTableCheckboxes(rowIds: string[], defaultSelected: string[] = []) {
  const [lastChecked, setLastChecked] = useState<string | undefined>(undefined);
  const [checkedBoxes, setCheckedBoxes] = useState<string[]>(defaultSelected);

  // If checkedBoxes contains ids of rows that are not in 'rows' anymore, remove them.
  const newChecked = [...checkedBoxes].filter((id) => rowIds.includes(id));
  if (newChecked.length !== checkedBoxes.length) {
    setCheckedBoxes(newChecked);
  }

  const rowChecked = (rowId: string) => checkedBoxes.includes(rowId);

  const setChecked = (rowId: string, event: React.MouseEvent<HTMLElement>) => {
    const shiftKey = event.shiftKey;

    if (!shiftKey || !checkedBoxes.length) {
      setLastChecked(rowId);
      return setCheckedBoxes((prev) =>
        prev.includes(rowId) ? prev.filter((id) => id !== rowId) : makeUnique([...prev, rowId])
      );
    }

    const addCheckBoxes: string[] = [];
    let addingCheckBoxes = false;
    let hasUnselectedBox = false;

    const addCheckbox = (id: string) => {
      addCheckBoxes.push(id);
      if (!checkedBoxes.includes(id)) hasUnselectedBox = true;
    };

    rowIds.forEach((id) => {
      if (addingCheckBoxes) addCheckbox(id);
      if ((id === lastChecked || id === rowId) && lastChecked !== rowId)
        addingCheckBoxes = !addingCheckBoxes;
      if (addingCheckBoxes) addCheckbox(id);
    });

    setCheckedBoxes((prev) =>
      makeUnique(
        hasUnselectedBox
          ? makeUnique([...prev, ...addCheckBoxes])
          : prev.filter((id) => addCheckBoxes.includes(id))
      )
    );
  };

  const selectOnly = (rowId: string) => {
    setCheckedBoxes([rowId]);
  };

  const allChecked = rowIds.length > 0 && rowIds.every(rowChecked);
  const someChecked = rowIds.some(rowChecked);

  const setAllChecked = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setCheckedBoxes([...rowIds]);
    } else {
      setCheckedBoxes([]);
    }
  };

  const deselectAll = () => {
    setCheckedBoxes([]);
  };

  const checkedRows = rowIds.filter(rowChecked);

  return {
    rowChecked,
    selectOnly,
    setChecked,
    allChecked,
    someChecked,
    setAllChecked,
    deselectAll,
    checkedRows,
  };
}
